import queryString from 'query-string'
import React from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import styled from 'styled-components'

import { borderColor } from '../../common/colors'
import GoogleTagManager from '../../common/components/GoogleTagManager'
import LoadingOverlay from '../../common/components/LoadingOverlay'
import {
  addToCart,
  AddToCartResponse,
  getCurrentUser,
  getCustomLabelByOrganizationSlug,
  getMembershipsByOrganizationId,
  getMembershipTemplateOffers,
} from '../../common/requests'
import { routes } from '../../common/routes'
import { Button, LinkButton, PrimaryButton } from '../../common/styled-components/buttons'
import { FullWidth, ViewContainer } from '../../common/styled-components/layout'
import { TitleTab } from '../../common/styled-components/title-tab'
import { H1, ReturnArrow, SecondaryText } from '../../common/styled-components/typography'
import FamilyMember from '../../dtos/FamilyMember'
import Membership from '../../dtos/Membership'
import Offer from '../../dtos/Offer'
import { IGroup, IOffer, IOfferOption } from '../../dtos/Wish'
import CompleteSessionOffer from '../groupsAndOffers/complete-sessions/CompleteSessionOffer'
import { IFamilyMember } from '../wishlist/types'
import { MembershipItem } from './MembershipItem'
import {CUSTOM_LABELS_JSON_TEMPLATE} from "../../utils/customLabelsTemplate";
import CustomLabels from "../../dtos/CustomLabels";

interface IMembershipsProps extends RouteComponentProps, WithTranslation {
  organizationId: number
  organizationSlug?: string
  shopUrl:        string | null
  altTranslationKeys: string[]
  gtag:        string | null
}

enum ECurrentStep {
  MEMBERSHIPS =         'memberships',
  RELATED_OFFERS = 'related-offers'
}

interface ITransformedWishDataItem extends IGroup {
  wishId:             number
  subscriber_user_id: number | null
  plan_id:            number | null
}

interface ITransformedWishData {
  id:                 number
  title:              string
  description:        string
  subscriber_user_id: number | null
  items:              ITransformedWishDataItem[],
  offer_data:         IOffer
}

interface IMembershipsState {
  membershipId: number | undefined
  noData:     boolean
  items:      Record<string, ITransformedWishData>
  loading:    boolean
  btnLoading: boolean
  selectedOffers: Record<number, IOfferOption>
  isLoggedIn:          boolean
  memberships:         Membership[] | undefined
  currentStep:         ECurrentStep,
  offers:              Offer[]
  amountOfOffersSteps: number
  currentOfferStep:    number
  users:               IFamilyMember[]
  familyMembers:       Record<string, Partial<FamilyMember>>
  userId: number|null
  hasContact: boolean
  customLabels: CustomLabels
}

const ContentContainer = styled.div`
  padding: 30px 60px;
`

const StyledLinkButton = styled(LinkButton)`
  background-color: #F40B17;

  &:hover {
    background-color: #ff1f2b;
  }
`

const ButtonsContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
  width: 100%;
  padding-left: 60px;
  padding-right: 60px;
  ${Button} + ${Button},
  ${StyledLinkButton} + ${Button},
  ${Button} + ${StyledLinkButton},
  ${StyledLinkButton} + ${StyledLinkButton} {
    margin-left: 30px;
  }
`

const LoadingOverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`

class Memberships extends React.Component<IMembershipsProps, IMembershipsState> {
  state: IMembershipsState = {
    membershipId: undefined,
    familyMembers: {},
    offers:       [],
    noData:              true,
    items:               {},
    loading:             true,
    btnLoading:          false,
    isLoggedIn:          false,
    selectedOffers:      {},
    memberships:         undefined,
    currentStep:         ECurrentStep.MEMBERSHIPS,
    amountOfOffersSteps: 0,
    currentOfferStep:    0,
    users:               [],
    userId: null,
    hasContact: false,
    customLabels: {}
  }

  async componentDidMount() {
    const params = queryString.parse(this.props.location.search)
    const membershipsToShow = typeof params.membershipTemplate_ids === 'string'
      ? [params.membershipTemplate_ids]
      : params.membershipTemplate_ids || []
    const clientData = await getMembershipsByOrganizationId(this.props.organizationId)

    const membershipTemplates = clientData.membership_templates
      .filter(x => membershipsToShow.includes(String(x.id)))

    if (membershipTemplates.length === 0) {
      this.props.history.push(routes.shoppingCart)
    }

    const user = await getCurrentUser()

    let customLabels: CustomLabels = {}
    if (this.props.organizationSlug) {
        try {
            customLabels = await getCustomLabelByOrganizationSlug(
                this.props.organizationSlug,
                [
                    CUSTOM_LABELS_JSON_TEMPLATE[0]
                ]
            );
        } catch {
            customLabels = {}
        }
    }

    this.setState({
      isLoggedIn: Boolean(user),
      memberships: membershipTemplates,
      customLabels: customLabels,
      noData:  false,
      loading: false,
      userId: undefined !== user && undefined !== user.id ? user.id : null,
      hasContact: user !== undefined
        && undefined !== user.contacts
        && undefined !== user.contacts.find((contact) => contact.organization_id === this.props.organizationId)
    })
  }

  goToLogin = () => {
    const redirectUrl = encodeURIComponent(`${this.props.location.pathname}${this.props.location.search}${this.props.location.hash}`)
    this.props.history.push(`${routes.authentication}/${routes.authenticationSubRoutes.login}?redirect_url=${redirectUrl}`)
  }

  handleMembershipSelected = async (membershipId: number) => {
    if (!this.state.isLoggedIn) {
      this.goToLogin()
    } else {
      const offers = await getMembershipTemplateOffers(membershipId)
      if (!offers.familyMembers) {
        throw new Error('No family members')
      }
      this.setState({
        membershipId,
        offers: offers.offers,
        familyMembers: offers.familyMembers,
        selectedOffers: {},
        currentStep: ECurrentStep.RELATED_OFFERS
      })
    }
  }

  navigateBackToGroups = () => {
    this.setState({
      currentStep: ECurrentStep.MEMBERSHIPS,
      selectedOffers: {},
      offers: []
    })
  }

  addToCart = async () => {

    this.setState({
      loading: true
    })

    for (const [planId, option] of Object.entries(this.state.selectedOffers)) {
      await addToCart(option.subscriber, Number(planId));
    }

    this.setState({
      loading: false
    })
    this.props.history.push(routes.shoppingCart)
  }

  handleOptionSelected = (option: IOfferOption, offerId: number) => {
    this.setState({
      selectedOffers: {
        ...this.state.selectedOffers,
        [offerId]: option
      }
    })
  }

  addSessionToCart = async (userId: number, planId: number, successMessage?: string): Promise<AddToCartResponse> => {
    this.setState({
      loading: true
    })
    const cartItem = await addToCart(userId, planId, {});
    // const {offers: returnedOffers} = await getSessionOffersForGroup(groupId, {redirectToLogin});
    // TODO: update offers?
    // setOffers(returnedOffers);
    this.setState({
      loading: false
    })
    this.props.history.push(routes.shoppingCart)
    return cartItem;
  }

  removeSessionFromCart = async (itemId: number) => {
    this.setState({
      loading: true
    })
    // await deleteCartItem(itemId, {});
    // const {offers: returnedOffers} = await getSessionOffersForGroup(groupId, {redirectToLogin});
    // setOffers(returnedOffers);
    this.setState({
      loading: false
    })
  }

  render() {
    const { t } = this.props
    if (this.state.loading) {
      return (
        <section>
          <LoadingOverlayContainer>
            <LoadingOverlay />
          </LoadingOverlayContainer>
          <TitleTab>
            <h1>{t('wishlist.loading')}</h1>
          </TitleTab>
        </section>
      )
    }

    switch (this.state.currentStep) {
      case ECurrentStep.MEMBERSHIPS: {
        return (
          <section>
            <GoogleTagManager gtag={this.props.gtag} />
            <TitleTab>
              <h1>
                {
                  this.state.isLoggedIn && this.state.customLabels[CUSTOM_LABELS_JSON_TEMPLATE[0]]
                    ? this.state.customLabels[CUSTOM_LABELS_JSON_TEMPLATE[0]]
                    : t(this.state.isLoggedIn ? 'memberships.title' : 'wishlist.pleaseLogin')
                }
              </h1>
            </TitleTab>
            <ContentContainer>
              <div>
                {
                  this.state.memberships?.map(x =>
                    <MembershipItem
                      key={x.id}
                      membership={x}
                      onSelection={this.handleMembershipSelected}
                      altTranslationKeys={this.props.altTranslationKeys}
                    />
                  )
                }
              </div>
              <ButtonsContainer>
                {
                  this.state.isLoggedIn
                    ? null
                    : (
                      <PrimaryButton
                        onClick={this.goToLogin}
                        type="button"
                      >
                        {t('wishlist.goToLogin')}
                      </PrimaryButton>
                    )
                }
              </ButtonsContainer>
            </ContentContainer>
          </section>
        )
      }
      case ECurrentStep.RELATED_OFFERS: {
        return (
          <>
            <GoogleTagManager gtag={this.props.gtag} />
            <GroupOffersTitleTab>
                <ReturnButton onClick={this.navigateBackToGroups}><ReturnArrow /> {t('return')}</ReturnButton>
                <TabTitle>{t('offers.title')}</TabTitle>
            </GroupOffersTitleTab>
            <GroupViewContainer>
              {
                this.state.familyMembers && (this.state.offers.map((offer, index) => (
                  this.state.userId && <FullWidth key={`offer-${index}`}>
                    <OfferContainer>
                      <CompleteSessionOffer
                        offer={offer}
                        familyMembers={this.state.familyMembers}
                        addSessionToCart={this.addSessionToCart}
                        removeSessionFromCart={this.removeSessionFromCart}
                        userId={this.state.userId}
                        organizationId={this.props.organizationId}
                        hasContact={this.state.hasContact}
                      />
                    </OfferContainer>
                  </FullWidth>
                )))
              }
            </GroupViewContainer>
          </>
        )
      }
    }
  }
}

export default withRouter(withTranslation()(Memberships));

const GroupViewContainer = styled(ViewContainer)`
  text-align: left;
  padding: 0 !important;
  align-items: flex-start;
`;

const TabTitle = styled(H1)`
  margin: 0 !important;
`;

const GroupOffersTitleTab = styled.div`
  width: 100%;
  background: #F5F5F5;
  box-shadow: 0 2px 8px 0 rgba(0,0,0,0.30);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 80px;
  position: relative;
`;
const OfferContainer = styled(ViewContainer)`
  border-top: 1px solid ${borderColor};
  align-items: flex-start;
`;

const ReturnButton = styled(SecondaryText)`
  position: absolute;
  top: calc(50% - 8px);
  left: 60px;
  cursor: pointer;
`;
